import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateCommentSchema, type TCreateComment, type TCommentBase } from '@onesource/schemas';
import { useGetComments, useCreateComment } from '@onesource/services';
import { format } from 'date-fns';
import { ReactionBar } from './reaction-bar';

interface CommentThreadProps {
  postId: string;
}

/**
 * Comment Thread Component
 * Displays all comments for a post and allows adding new comments
 */
export const CommentThread = ({ postId }: CommentThreadProps) => {
  const { data: comments, isLoading, error } = useGetComments(postId);
  const createCommentMutation = useCreateComment(postId);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<TCreateComment>({
    resolver: zodResolver(CreateCommentSchema),
    defaultValues: {
      body: '',
    },
  });

  const onSubmit = async (data: TCreateComment) => {
    try {
      setSubmitError(null);
      await createCommentMutation.mutateAsync(data);
      reset();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      setSubmitError(axiosError?.response?.data?.message || 'Failed to create comment');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Comments ({comments?.length || 0})
      </Typography>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={2} mb={3}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <Controller
            name="body"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Add a comment"
                multiline
                rows={3}
                fullWidth
                required
                error={!!errors.body}
                helperText={errors.body?.message}
                placeholder="Share your thoughts..."
              />
            )}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </Box>
        </Box>
      </form>

      <Divider sx={{ my: 3 }} />

      {/* Comments List */}
      {isLoading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">Failed to load comments: {error.message}</Alert>}

      {comments && comments.length === 0 && <Alert severity="info">No comments yet. Be the first to comment!</Alert>}

      {comments && comments.length > 0 && (
        <Stack spacing={2}>
          {comments.map((comment: TCommentBase) => (
            <Card key={comment.id} variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(comment.createdAt), 'MMM d, yyyy • h:mm a')}
                  </Typography>
                </Box>

                {/* Comment Body */}
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                  {comment.body}
                </Typography>

                {/* Reaction Bar */}
                <ReactionBar targetId={comment.id} targetType="comment" likeCount={comment.likeCount} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};
